import { forwardRef, useState, useEffect, useCallback, useRef, Ref, RefAttributes, type JSX } from "react";

import {
  convertCurrencyToCurrencySymbol,
  formatDisplayValue,
  getZeroAmountPlaceholder,
  isCurrencySymbolAfterAmount,
  parseDefaultDisplayValue,
  parseInputValue,
  sanitizeAmountInput,
  stripGroupSeparators,
} from "@simplyk/common";
import { FieldValues, useController, useWatch } from "react-hook-form";

import { FormAmountFieldProps, SubscribedFormAmountFieldProps, BaseFormAmountFieldProps } from "./types";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { blockNonAmountKeys } from "@/common/helpers/blockNonAmountKeys";
import { useValidators } from "@/common/hooks/useValidators";
import { FormTextField } from "@/forms-design-system/FormTextField";

function FormAmountFieldInner<T extends FieldValues>(
  { control, rules, name, shouldUnregister, ...rest }: FormAmountFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <SubscribedFormAmountField
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
    />
  ) : (
    <BaseFormAmountField {...rest} name={name} ref={ref} />
  );
}

FormAmountFieldInner.displayName = "FormAmountField";

export const FormAmountField = forwardRef(FormAmountFieldInner) as <T extends FieldValues>(
  props: FormAmountFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

function SubscribedFormAmountFieldInner<T extends FieldValues>(
  {
    control,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    ...rest
  }: SubscribedFormAmountFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);
  const watchedValue = useWatch({ control, name });
  const internalDefaultValue = defaultValue || watchedValue;

  const { field } = useController<T>({
    name,
    control,
    defaultValue: internalDefaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const handleChange = useCallback(
    (centsValue: number | null) => {
      onChange?.(centsValue);
      field.onChange(centsValue);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      field.onBlur();
    },
    [field, onBlur]
  );

  return (
    <BaseFormAmountField
      {...rest}
      value={field.value}
      inputRef={inputRef}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-amount-field"}
      name={name}
    />
  );
}

const SubscribedFormAmountField = forwardRef(SubscribedFormAmountFieldInner) as <T extends FieldValues>(
  props: SubscribedFormAmountFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const BaseFormAmountField = forwardRef<HTMLInputElement, BaseFormAmountFieldProps>(
  (
    {
      value = null,
      onChange,
      onBlur,
      onFocus,
      hideAdornment = false,
      inputRef,
      currency,
      placeholder,
      inputProps,
      ...rest
    },
    ref
  ) => {
    const { locale } = useLocaleContext();
    const [internalValue, setInternalValue] = useState<string>(() =>
      parseDefaultDisplayValue(value ?? undefined, locale)
    );
    const isFocusedRef = useRef(false);
    const previousLocaleRef = useRef(locale);

    useEffect(() => {
      if (parseInputValue(internalValue, locale) !== value) {
        setInternalValue(parseDefaultDisplayValue(value ?? undefined, locale));
      }
    }, [value, internalValue, locale]);

    useEffect(() => {
      if (previousLocaleRef.current === locale) {
        return;
      }
      previousLocaleRef.current = locale;
      if (!isFocusedRef.current) {
        setInternalValue(parseDefaultDisplayValue(value ?? undefined, locale));
      }
    }, [locale, value]);

    const currencySymbol = currency ? convertCurrencyToCurrencySymbol(currency) : "$";
    const isSymbolAfterAmount = isCurrencySymbolAfterAmount(locale);

    return (
      <FormTextField
        {...rest}
        type="text"
        inputProps={{ inputMode: "decimal", onKeyPress: blockNonAmountKeys, ...inputProps }}
        placeholder={placeholder ?? getZeroAmountPlaceholder(locale)}
        startAdornment={hideAdornment || isSymbolAfterAmount ? undefined : currencySymbol}
        endAdornment={hideAdornment || !isSymbolAfterAmount ? undefined : currencySymbol}
        value={internalValue}
        onChange={(e) => {
          const sanitizedValue = sanitizeAmountInput(e.target.value);
          setInternalValue(sanitizedValue);
          onChange?.(parseInputValue(sanitizedValue, locale));
        }}
        onFocus={(e) => {
          isFocusedRef.current = true;
          setInternalValue((currentValue) => stripGroupSeparators(currentValue, locale));
          onFocus?.(e);
        }}
        onBlur={(e) => {
          isFocusedRef.current = false;
          setInternalValue((currentValue) => formatDisplayValue(currentValue, locale));
          onBlur?.(e);
        }}
        inputRef={inputRef}
        ref={ref}
      />
    );
  }
);

BaseFormAmountField.displayName = "BaseFormAmountField";
